import { Box, Button, Grid, Typography } from '@mui/material';
import React from 'react';
import { BigLogo, TitleLoginGridItem } from './components/Header/styles.ts';
import { useMsal } from '@azure/msal-react';
import type { IPublicClientApplication, ServerError, AuthenticationResult } from '@azure/msal-browser';
import { PublicClientApplication } from '@azure/msal-browser';
import { Lock } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import {
  loginRequest as _loginRequest,
  guestMsalConfig,
  backendMsalConfig,
  graphLoginRequest,
  frontendMsalConfig,
  msalConfig,
  getDynamicLoginRequest,
  clearLogoutFlag,
  shouldAttemptSilentAuth,
  apiLoginRequest
} from '@/authConfig.ts';
import { useLocation as _useLocation } from 'react-router-dom';
import { isHeiDomain } from 'app/utils/heiDomain';
import { authTelemetry } from '@/app/services/authTelemetry';

const useExternalInstance =
  window.location.search === '?external=true' || window.location.pathname === '/loginExternal';

const MicrosoftLoginButton = (props: { onClick: () => void | Promise<void>; withMicrosoft?: boolean }) => {
  const { onClick, withMicrosoft: _withMicrosoft } = props;
  return (
    <StyledButton variant="contained" color="primary" startIcon={<Lock />} onClick={onClick}>
      Sign in
    </StyledButton>
  );
};

const StyledButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(2),
  padding: theme.spacing(1.5, 4),
  fontSize: '1rem',
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: 30,
  boxShadow: '0 3px 5px 2px rgb(248, 160, 117, .5) ',
  background: 'linear-gradient(45deg, #333333 30%, #555555 90%)',
  color: 'white',
  '&:hover': {
    background: 'linear-gradient(45deg, #222222 30%, #222222 20%)',
    boxShadow: '0 3px 5px 2px rgba(248, 160, 117)'
  }
}));

const AppUnauthenticated = () => {
  const { instance } = useMsal();
  const [needsBackendConsent, setNeedsBackendConsent] = React.useState(false);

  // Capture the current requested path (excluding special routes)
  React.useEffect(() => {
    const path = window.location.pathname + window.location.search + window.location.hash;
    if (path !== '/' && path !== '/loginExternal') {
      localStorage.setItem('postLoginRedirectPath', path);
    }
  }, []);

  const loginPopupReact = async (providedInstance: IPublicClientApplication, guest = false) => {
    try {
      // For returning users, try silent authentication first
      if (shouldAttemptSilentAuth()) {
        try {
          const accounts = providedInstance.getAllAccounts();
          if (accounts.length > 0) {
            const account = accounts[0];
            const silentRequest = {
              ...apiLoginRequest,
              account: account,
              authority: guest ? guestMsalConfig.auth.authority : msalConfig.auth.authority
            };

            const silentResponse = await providedInstance.acquireTokenSilent(silentRequest);

            // Track successful silent authentication
            if (silentResponse && account) {
              authTelemetry.trackSessionStart(account, 'silent');
            }

            // Clear logout flag after successful silent auth
            clearLogoutFlag();

            let redirectPath = localStorage.getItem('postLoginRedirectPath') || '/';
            if (isHeiDomain(window.location.hostname)) {
              redirectPath = '/translator';
            }
            localStorage.removeItem('postLoginRedirectPath');
            window.location.href = redirectPath;
            return;
          }
        } catch (silentError) {
          // Track silent authentication failure
          authTelemetry.trackSilentAuthFailure(silentError, 'popup');
        }
      }

      // If silent auth fails or user logged out, use appropriate popup strategy
      const accounts = providedInstance.getAllAccounts();
      const dynamicRequest = getDynamicLoginRequest(accounts.length, accounts[0]);

      let loginResponse: AuthenticationResult;
      if (guest) {
        loginResponse = await providedInstance.loginPopup({
          ...dynamicRequest,
          authority: guestMsalConfig.auth.authority
        });
      } else {
        loginResponse = await providedInstance.loginPopup({ ...dynamicRequest, authority: msalConfig.auth.authority });
      }

      // Track successful interactive authentication
      if (loginResponse && loginResponse.account) {
        authTelemetry.trackInteractiveAuth('popup', shouldAttemptSilentAuth() ? 'silent_failed' : 'user_initiated');
        authTelemetry.trackSessionStart(loginResponse.account, 'popup');
      }

      // Clear logout flag after successful login
      clearLogoutFlag();

      let redirectPath = localStorage.getItem('postLoginRedirectPath') || '/';
      if (isHeiDomain(window.location.hostname)) {
        redirectPath = '/translator';
      }
      localStorage.removeItem('postLoginRedirectPath');
      window.location.href = redirectPath;
    } catch (e) {
      const error = e as ServerError;

      // Track authentication error
      authTelemetry.trackInteractiveAuth('popup', 'user_initiated');

      if (error.errorMessage.startsWith('AADSTS650052')) {
        setNeedsBackendConsent(true);
      }
      // Error is already tracked via telemetry
    }
  };

  const _loginFrontendPopup = async () => {
    const frontendInstance = new PublicClientApplication(useExternalInstance ? frontendMsalConfig : guestMsalConfig);
    try {
      await frontendInstance.loginPopup(graphLoginRequest);
      let redirectPath = localStorage.getItem('postLoginRedirectPath') || '/';
      if (isHeiDomain(window.location.hostname)) {
        redirectPath = '/translator';
      }
      localStorage.removeItem('postLoginRedirectPath');
      window.location.href = redirectPath;
    } catch (_e) {
      // Error handling could be added here if needed
    }
  };

  const loginBackendPopup = async () => {
    const backendInstance = new PublicClientApplication(
      useExternalInstance
        ? backendMsalConfig
        : { auth: { ...backendMsalConfig.auth, authority: guestMsalConfig.auth.authority } }
    );
    try {
      await backendInstance.loginPopup(graphLoginRequest);
      let redirectPath = localStorage.getItem('postLoginRedirectPath') || '/';
      if (isHeiDomain(window.location.hostname)) {
        redirectPath = '/translator';
      }
      localStorage.removeItem('postLoginRedirectPath');
      window.location.href = redirectPath;
    } catch (_e) {
      // Error handling could be added here if needed
    }
  };

  return (
    <Box height="100vh">
      <Grid container direction="column" justifyContent="center" alignItems="center" style={{ height: '70%' }}>
        <Grid item component={TitleLoginGridItem}>
          <BigLogo />
        </Grid>
        {needsBackendConsent ? (
          <>
            <Grid item>
              <Typography variant="h5" gutterBottom>
                You need to consent to additional information to use the app:
              </Typography>
            </Grid>
            <Grid item>
              <Button variant="contained" color="primary" onClick={() => loginBackendPopup()}>
                Sign in and Consent
              </Button>
            </Grid>
          </>
        ) : (
          <>
            {useExternalInstance ? (
              <>
                {/* <Grid item>
                  <Typography variant="h6">Work or school account:</Typography>
                </Grid> */}
                <Grid item>
                  <MicrosoftLoginButton onClick={() => loginPopupReact(instance)} withMicrosoft />
                </Grid>
                {/* <Grid item style={{ marginTop: 30 }}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => (window.location.href = '/')}
                  >
                    Go to guest login
                  </Button>
                </Grid> */}
              </>
            ) : (
              <>
                {/* <Grid item>
                  <Typography variant="h6">Sklls account:</Typography>
                </Grid> */}
                <Grid item sx={{ marginTop: 6 }}>
                  <MicrosoftLoginButton onClick={() => loginPopupReact(instance, true)} />
                  {/* <Button variant="contained" color="secondary" onClick={() => loginPopupReact(instance, true)}>
                Sign in as Guest in Sklls Tenant
              </Button> */}
                </Grid>
                {/* <Grid item style={{ marginTop: 10 }}>
                  <Typography variant="h6">Work or school account:</Typography>
                </Grid> */}
                {/* <Grid item>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => (window.location.href = '/loginExternal')}
                  >
                    Sign in with a work or school account
                  </Button>
                </Grid> */}
              </>
            )}
          </>
        )}
      </Grid>
    </Box>
  );
};

export default AppUnauthenticated;
